/**
 * Darstellung (Hell / Dunkel / System). Die Auswahl wird in localStorage gehalten
 * und als data-theme am Wurzelelement gesetzt; die Farben kommen aus tokens.css.
 */

import { useCallback, useEffect, useState } from 'react';

export type Darstellung = 'system' | 'hell' | 'dunkel';

const SPEICHER_SCHLUESSEL = 'nkp.darstellung';

export function ladeDarstellung(): Darstellung {
  try {
    const wert = localStorage.getItem(SPEICHER_SCHLUESSEL);
    if (wert === 'hell' || wert === 'dunkel' || wert === 'system') return wert;
  } catch {
    /* Speicher nicht verfügbar (privater Modus, Webview-Beschränkung). */
  }
  return 'system';
}

function speichereDarstellung(wahl: Darstellung): void {
  try {
    localStorage.setItem(SPEICHER_SCHLUESSEL, wahl);
  } catch {
    /* Ohne Speicher gilt die Auswahl nur für die laufende Sitzung. */
  }
}

export function wendeDarstellungAn(wahl: Darstellung): void {
  const wurzel = document.documentElement;
  if (wahl === 'system') wurzel.removeAttribute('data-theme');
  else wurzel.setAttribute('data-theme', wahl === 'dunkel' ? 'dark' : 'light');
}

function systemIstDunkel(): boolean {
  return typeof window !== 'undefined'
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false;
}

export interface DarstellungSteuerung {
  wahl: Darstellung;
  istDunkel: boolean;
  setzeWahl: (wahl: Darstellung) => void;
  umschalten: () => void;
}

export function useDarstellung(): DarstellungSteuerung {
  const [wahl, setWahl] = useState<Darstellung>(() => ladeDarstellung());
  const [systemDunkel, setSystemDunkel] = useState<boolean>(() => systemIstDunkel());

  useEffect(() => {
    wendeDarstellungAn(wahl);
  }, [wahl]);

  useEffect(() => {
    const abfrage = window.matchMedia('(prefers-color-scheme: dark)');
    const beiAenderung = (e: MediaQueryListEvent) => setSystemDunkel(e.matches);
    abfrage.addEventListener('change', beiAenderung);
    return () => abfrage.removeEventListener('change', beiAenderung);
  }, []);

  const setzeWahl = useCallback((neu: Darstellung) => {
    setWahl(neu);
    speichereDarstellung(neu);
  }, []);

  const istDunkel = wahl === 'dunkel' || (wahl === 'system' && systemDunkel);

  const umschalten = useCallback(() => {
    setzeWahl(istDunkel ? 'hell' : 'dunkel');
  }, [istDunkel, setzeWahl]);

  return { wahl, istDunkel, setzeWahl, umschalten };
}
